import React, { useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { Loop as LoopIcon } from '@mui/icons-material';
import { useRecurringItems } from '../contexts/RecurringItemsContext';
import { formatRecurringDays, formatMonthDays } from '../utils/recurringUtils';

interface RecurringItemBadgeProps {
  recurringMetadataId: string;
}

/**
 * Displays a badge showing recurring schedule information for an item
 */
const RecurringItemBadge: React.FC<RecurringItemBadgeProps> = ({ recurringMetadataId }) => {
  const { getRecurringItem, getRecurringItemAsync } = useRecurringItems();
  const [weekdays, setWeekdays] = useState<number[] | undefined>();
  const [monthDays, setMonthDays] = useState<number[] | undefined>();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Try to get from cache first
    const cachedItem = getRecurringItem(recurringMetadataId);
    if (cachedItem) {
      setWeekdays(cachedItem.weekdays);
      setMonthDays(cachedItem.monthDays);
      setNotFound(false);
    } else if (!notFound) {
      // Fetch if not in cache and not previously marked as not found
      getRecurringItemAsync(recurringMetadataId).then(item => {
        if (item) {
          setWeekdays(item.weekdays);
          setMonthDays(item.monthDays);
          setNotFound(false);
        } else {
          // Item not found (404 or other error) - stop trying to fetch
          setNotFound(true);
        }
      });
    }
  }, [recurringMetadataId, getRecurringItem, getRecurringItemAsync, notFound]);
  
  // Don't show badge if item not found or no schedule is defined
  if (notFound) {
    return null;
  }

  const hasSchedule = (weekdays && weekdays.length > 0) || (monthDays && monthDays.length > 0);
  if (!hasSchedule) {
    return null;
  }

  const label = weekdays && weekdays.length > 0
    ? formatRecurringDays(weekdays)
    : monthDays && monthDays.length > 0
    ? formatMonthDays(monthDays)
    : '';

  return (
    <Chip
      icon={<LoopIcon sx={{ fontSize: '0.875rem' }} />}
      label={label}
      size="small"
      sx={{
        height: '20px',
        fontSize: '0.7rem',
        '& .MuiChip-icon': {
          marginLeft: '4px',
        },
        '& .MuiChip-label': {
          paddingLeft: '4px',
          paddingRight: '6px',
        },
      }}
    />
  );
};

export default RecurringItemBadge;
